import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  AreaChart, 
  Area, 
  ResponsiveContainer, 
  BarChart, 
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip
} from 'recharts';
import { 
  Fuel, 
  Briefcase,
  Megaphone,
  Package,
  ShieldCheck, 
  Users, 
  ClipboardList, 
  Home, 
  Zap, 
  Globe, 
  ShoppingCart, 
  DollarSign,
  AlertTriangle
} from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { ProtectedFinancialCard } from '../../components/ProtectedSensitiveData';
import './Inventaire.css';

import Swal from 'sweetalert2';

const getApiBase = () => {
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : apiUrl + '/api';
};

const API_BASE = getApiBase();
const authHeader = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

const CATEGORY_CONFIG = {
  'Equipe installation': { icon: <Users size={18} />, color: '#ED8936', budget: 15000 },
  'Carburant': { icon: <Fuel size={18} />, color: '#3182CE', budget: 5000 },
  'Assurance / Vignette': { icon: <ShieldCheck size={18} />, color: '#805AD5', budget: 2000 },
  'Loyer': { icon: <Home size={18} />, color: '#EA580C', budget: 5000 },
  'STEG & SONEDE': { icon: <Zap size={18} />, color: '#6366F1', budget: 4000 },
  'Connexion Internet': { icon: <Globe size={18} />, color: '#3B82F6', budget: 1000 },
  'Achat des Produits': { icon: <ShoppingCart size={18} />, color: '#EC4899', budget: 8000 },
  'Divers': { icon: <Briefcase size={18} />, color: '#0EA5E9', budget: 0 },
  'Sponsoring / Marketing': { icon: <Megaphone size={18} />, color: '#F59E0B', budget: 0 },
  'Fournisseurs': { icon: <Package size={18} />, color: '#14B8A6', budget: 0 },
  'Bureau': { icon: <ClipboardList size={18} />, color: '#64748B', budget: 0 },
  'Salaire employés': { icon: <Users size={18} />, color: '#8B5CF6', budget: 40000 },
  'DF (Dépenses Fixes)': { icon: <ClipboardList size={18} />, color: '#10B981', budget: 12000 },
  // Legacy mappings to ensure old data shows icons
  'Voiture': { icon: <Fuel size={18} />, color: '#3182CE', budget: 5000 },
  'Achat / Problèmes': { icon: <ShoppingCart size={18} />, color: '#EC4899', budget: 8000 },
  'Achats Fournisseurs': { icon: <Package size={18} />, color: '#14B8A6', budget: 0 },
  'Dépenses générales': { icon: <Zap size={18} />, color: '#6366F1', budget: 4000 },
  'Dépenses client (DF)': { icon: <ClipboardList size={18} />, color: '#10B981', budget: 12000 }
};

const SPARKLINE_DATA = [
  { value: 40 }, { value: 65 }, { value: 55 }, { value: 85 }, { value: 75 }, { value: 95 }
];

const DIVERS_SUBCATEGORIES = [
  'Transport',
  'Avocat',
  'Piscine',
  'Maintenance',
  'Sécurité',
  'Autre'
];

const FOURNISSEUR_TYPES = [
  'Prestataire',
  'Matériel',
  'Service',
  'Autre'
];

import { PAYMENT_MODES, PAYMENT_MODE_LABELS, getPaymentModeLabel } from '../../utils/paymentModes';

const MODE_PAIEMENT_OPTIONS = PAYMENT_MODES;
const MODE_PAIEMENT_LABELS = PAYMENT_MODE_LABELS;

const Inventaire = ({ onNavigate, onLogout, currentPage }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('month'); // 'all', 'day', 'week', 'month', 'year', 'custom'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('current'); // 'current' | 'history'
  
  const [categoryBudgets, setCategoryBudgets] = useState(() => {
    const saved = localStorage.getItem('sastra_category_budgets');
    return saved ? JSON.parse(saved) : {
      'Equipe installation': 15000,
      'Voiture': 5000,
      'Achat / Problèmes': 8000,
      'Dépenses générales': 10000,
      'Divers': 0,
      'Sponsoring / Marketing': 0,
      'Fournisseurs': 0,
      'Bureau': 0,
      'Salaire employés': 40000,
      'Dépenses client (DF)': 12000,
      "Chiffre d'affaires": 100000
    };
  });

  const fetchBudgets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/budgets`, authHeader());
      if (res.data && Object.keys(res.data).length > 0) {
        setCategoryBudgets(res.data);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des budgets:", err);
    }
  }, []);

  useEffect(() => {
    fetchBudgets();
  }, [fetchBudgets]);

  const saveBudgets = async (category, amount) => {
    try {
      await axios.post(`${API_BASE}/budgets`, { category, amount }, authHeader());
      const newBudgets = { ...categoryBudgets, [category]: amount };
      setCategoryBudgets(newBudgets);
      localStorage.setItem('sastra_category_budgets', JSON.stringify(newBudgets));
    } catch (err) {
      console.error("Erreur lors de la sauvegarde du budget:", err);
      Swal.fire('Erreur', 'Impossible de sauvegarder le budget sur le serveur', 'error');
    }
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/inventaire/stats?period=${period}`;
      if (period === 'custom') {
        url += `&month=${selectedMonth}&year=${selectedYear}`;
      }
      const res = await axios.get(url, authHeader());
      setStats(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire('Erreur', 'Impossible de charger les statistiques', 'error');
    } finally {
      setLoading(false);
    }
  }, [period, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const items = useMemo(() => {
    if (!stats) return [];
    const cats = Object.entries(stats.categories).map(([name, data]) => {
      const config = CATEGORY_CONFIG[name] || { color: '#CBD5E0' };
      const amount = data.amount;
      const budget = categoryBudgets[name] || 0;
      const variation = amount - budget;
      const variationPercent = budget > 0 ? (variation / budget) * 100 : 0;

      return {
        id: name,
        category: name,
        amount: amount,
        count: data.count,
        type: 'Dépense',
        budget: budget,
        variation: variation,
        variationPercent: variationPercent,
        color: config.color,
        icon: config.icon
      };
    });

    cats.push({
        id: 'CA',
        category: "Chiffre d'affaires",
        amount: stats.chiffreAffaires,
        type: 'Revenu',
        budget: categoryBudgets["Chiffre d'affaires"] || 0,
        variation: stats.chiffreAffaires - (categoryBudgets["Chiffre d'affaires"] || 0),
        variationPercent: (categoryBudgets["Chiffre d'affaires"] || 0) > 0 ? ((stats.chiffreAffaires - categoryBudgets["Chiffre d'affaires"]) / categoryBudgets["Chiffre d'affaires"]) * 100 : 0,
        color: '#38A169',
        icon: <DollarSign size={20} />
    });

    return cats;
  }, [stats, categoryBudgets]);

  const filteredItems = useMemo(() => {
    return items.filter(item => 
      item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [items, searchTerm]);

  const pieData = useMemo(() => {
    return items
      .filter(i => i.type === 'Dépense')
      .map(i => ({ name: i.category, value: i.amount, color: i.color }));
  }, [items]);

  const [formState, setFormState] = useState({
    categorie: 'Equipe installation',
    type: '',
    montant: '',
    modePaiement: 'especes',
    nomRecepteur: '',
    notes: '',
    datePaiement: new Date().toISOString().split('T')[0]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientsForAssignment, setClientsForAssignment] = useState([]);
  const [commerciauxForAssignment, setCommerciauxForAssignment] = useState([]);
  const [commercialAssignment, setCommercialAssignment] = useState({
    clientId: '',
    dossierId: '',
    commercialId: ''
  });
  const [isAssigningCommercial, setIsAssigningCommercial] = useState(false);

  const fetchAssignmentHelpers = useCallback(async () => {
    try {
      const [clientsRes, commerciauxRes] = await Promise.all([
        axios.get(`${API_BASE}/clients?limit=1000`, authHeader()),
        axios.get(`${API_BASE}/clients/helpers/commerciaux`, authHeader())
      ]);
      setClientsForAssignment(clientsRes.data?.data || []);
      setCommerciauxForAssignment(commerciauxRes.data?.data || []);
    } catch (err) {
      console.error('Erreur chargement affectation commercial:', err);
    }
  }, []);

  useEffect(() => {
    fetchAssignmentHelpers();
  }, [fetchAssignmentHelpers]);

  const selectedAssignmentClient = useMemo(
    () => clientsForAssignment.find(client => client._id === commercialAssignment.clientId),
    [clientsForAssignment, commercialAssignment.clientId]
  );

  const assignmentDossiers = useMemo(
    () => (selectedAssignmentClient?.dossiers || []).filter(dossier => !dossier.isDeleted),
    [selectedAssignmentClient]
  );

  const selectedAssignmentDossier = useMemo(
    () => assignmentDossiers.find(dossier => dossier._id === commercialAssignment.dossierId),
    [assignmentDossiers, commercialAssignment.dossierId]
  );

  const resolveId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return value._id ? String(value._id) : '';
    return String(value);
  };

  const getModePaiementLabel = (mode) => MODE_PAIEMENT_LABELS[mode] || mode || '—';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'categorie') {
      const nextType = value === 'Divers'
        ? DIVERS_SUBCATEGORIES[0]
        : value === 'Fournisseurs'
          ? FOURNISSEUR_TYPES[0]
          : '';
      setFormState(prev => ({ ...prev, categorie: value, type: nextType }));
      return;
    }
    if (name === 'modePaiement') {
      setFormState(prev => ({
        ...prev,
        modePaiement: value,
        nomRecepteur: value === 'especes' ? prev.nomRecepteur : ''
      }));
      return;
    }
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const montantValue = parseFloat(formState.montant);
    if (!formState.type || Number.isNaN(montantValue) || montantValue <= 0) {
      return Swal.fire('Attention', 'Veuillez renseigner un type et un montant valide', 'warning');
    }
    if (formState.modePaiement === 'especes' && !(formState.nomRecepteur || '').trim()) {
      return Swal.fire('Attention', 'Le nom du recepteur est obligatoire pour les paiements en espèces', 'warning');
    }

    setIsSubmitting(true);
    try {
      const entiteType = formState.categorie === 'Voiture' ? 'Vehicule' : (formState.categorie === 'Equipe installation' ? 'Equipe' : 'General');
      await axios.post(`${API_BASE}/depenses`, {
        ...formState,
        montant: montantValue,
        entiteType,
        responsable: localStorage.getItem('userId'),
        datePaiement: new Date(formState.datePaiement)
      }, authHeader());
      
      Swal.fire({
        title: 'Succès',
        text: 'Dépense enregistrée avec succès',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      
      setFormState({
        categorie: 'Equipe installation',
        type: '',
        montant: '',
        modePaiement: 'especes',
        nomRecepteur: '',
        notes: '',
        datePaiement: new Date().toISOString().split('T')[0]
      });
      fetchStats();
    } catch (err) {
      console.error(err);
      Swal.fire('Erreur', 'Erreur lors de l\'enregistrement', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommercialAssignmentChange = (e) => {
    const { name, value } = e.target;
    if (name === 'clientId') {
      const client = clientsForAssignment.find(item => item._id === value);
      const dossier = (client?.dossiers || []).find(item => !item.isDeleted);
      setCommercialAssignment({
        clientId: value,
        dossierId: dossier?._id || '',
        commercialId: resolveId(dossier?.commercial)
      });
      return;
    }

    if (name === 'dossierId') {
      const dossier = assignmentDossiers.find(item => item._id === value);
      setCommercialAssignment(prev => ({
        ...prev,
        dossierId: value,
        commercialId: resolveId(dossier?.commercial)
      }));
      return;
    }

    setCommercialAssignment(prev => ({ ...prev, [name]: value }));
  };

  const handleCommercialAssignmentSubmit = async (e) => {
    e.preventDefault();
    if (!commercialAssignment.clientId || !commercialAssignment.dossierId || !commercialAssignment.commercialId) {
      return Swal.fire('Attention', 'Veuillez sélectionner un client, un dossier et un commercial', 'warning');
    }

    setIsAssigningCommercial(true);
    try {
      const res = await axios.post(
        `${API_BASE}/inventaire/clients/${commercialAssignment.clientId}/dossiers/${commercialAssignment.dossierId}/commercial`,
        { commercialId: commercialAssignment.commercialId },
        authHeader()
      );

      await Swal.fire({
        title: res.data?.warning ? 'Commercial affecté' : 'Succès',
        text: res.data?.warning?.message || 'Le commercial a été affecté avec succès.',
        icon: res.data?.warning ? 'warning' : 'success',
        confirmButtonText: 'Compris',
        confirmButtonColor: '#0F2343'
      });

      setCommercialAssignment({ clientId: '', dossierId: '', commercialId: '' });
      await fetchAssignmentHelpers();
      await fetchStats();
    } catch (err) {
      console.error(err);
      Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de l affectation du commercial', 'error');
    } finally {
      setIsAssigningCommercial(false);
    }
  };

  const handleViewDetails = (item) => {
    if (item.category === "Chiffre d'affaires") return;
    const catData = stats.categories[item.category];
    
    Swal.fire({
      title: `Détails: ${item.category}`,
      html: `
        <div style="text-align: left; line-height: 1.6; max-height: 400px; overflow-y: auto;">
          <p><b>Nombre d'entrées:</b> ${catData.count}</p>
          <p><b>Valeur Totale:</b> ${item.amount.toLocaleString()} DT</p>
          <hr/>
          <ul style="list-style: none; padding: 0;">
            ${catData.items.map(i => `
              <li style="margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #eee;">
                <div style="display: flex; justify-content: space-between;">
                  <span><b>${i.type || i.label || '—'}</b></span>
                  <span style="color: #E53E3E;">${i.montant.toLocaleString()} DT</span>
                </div>
                <div style="font-size: 11px; color: #718096;">
                  ${new Date(i.datePaiement || i.date).toLocaleDateString()} ${i.notes ? '- ' + i.notes : ''}
                </div>
                ${i.modePaiement ? `
                  <div style="font-size: 11px; color: #718096;">
                    Mode: ${getModePaiementLabel(i.modePaiement)}${i.nomRecepteur ? ` — Reçu par ${i.nomRecepteur}` : ''}
                  </div>
                ` : ''}
              </li>
            `).join('')}
          </ul>
        </div>
      `,
      confirmButtonText: 'Fermer',
      confirmButtonColor: '#0F2343'
    });
  };

  const handleEditBudget = (item) => {
    Swal.fire({
      title: `Modifier Objectif: ${item.category}`,
      input: 'number',
      inputLabel: item.type === 'Revenu' ? 'Revenu Minimum (DT)' : 'Budget Mensuel (DT)',
      inputValue: item.budget,
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      confirmButtonColor: '#0F2343',
      inputValidator: (value) => {
        if (!value || value < 0) return 'Veuillez saisir un montant valide';
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const newAmount = parseFloat(result.value);
        await saveBudgets(item.category, newAmount);
        Swal.fire('Succès', 'Objectif mis à jour.', 'success');
      }
    });
  };

  const isDivers = formState.categorie === 'Divers';
  const isFournisseur = formState.categorie === 'Fournisseurs';
  const requiresRecepteur = formState.modePaiement === 'especes';

  if (loading && !stats) return <LoadingSpinner message="Calcul de l'inventaire dynamique..." />;

  return (
    <div className="inv-dashboard-layout">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      
      <div className="inv-dashboard">
        <div className="inv-main-content">
          
          <div className="inv-hero animate-fade-in">
            <div className="inv-hero-text">
              <h1>Système d'Inventaire Dynamique</h1>
              <p>Suivi financier en temps réel basé sur les flux réels</p>
            </div>
          </div>

          <div className="inv-controls">
            <div className="inv-tabs">
               <button className={`inv-tab-btn ${activeTab === 'current' ? 'active' : ''}`} onClick={() => setActiveTab('current')}>Inventaire Actuel</button>
               <button className={`inv-tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>Historique d'archivage</button>
            </div>
            
            <div className="inv-action-buttons">
              <div className="inv-period-filter">
                <button className={period === 'all' ? 'active' : ''} onClick={() => setPeriod('all')}>Tous</button>
                <button className={period === 'day' ? 'active' : ''} onClick={() => setPeriod('day')}>Jour</button>
                <button className={period === 'week' ? 'active' : ''} onClick={() => setPeriod('week')}>Semaine</button>
                <button className={period === 'month' ? 'active' : ''} onClick={() => setPeriod('month')}>Mois Actuel</button>
                <button className={period === 'year' ? 'active' : ''} onClick={() => setPeriod('year')}>Année Actuelle</button>
                <button className={period === 'custom' ? 'active' : ''} onClick={() => setPeriod('custom')}>Historique</button>
              </div>

              {period === 'custom' && (
                <div className="inv-custom-picker">
                  <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                    <option value="1">Janvier</option>
                    <option value="2">Février</option>
                    <option value="3">Mars</option>
                    <option value="4">Avril</option>
                    <option value="5">Mai</option>
                    <option value="6">Juin</option>
                    <option value="7">Juillet</option>
                    <option value="8">Août</option>
                    <option value="9">Septembre</option>
                    <option value="10">Octobre</option>
                    <option value="11">Novembre</option>
                    <option value="12">Décembre</option>
                  </select>
                  <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              )}
              {activeTab === 'current' && (
                <div className="inv-status-label">Mode Edition Actif</div>
              )}
            </div>
          </div>

          <div className="inv-kpi-grid">
            <ProtectedFinancialCard title="Chiffre d'affaires protégé" lockedClassName="inv-kpi-card">
            <KPI_Card 
              label="Chiffre d'affaires"
              value={`${(stats?.chiffreAffaires || 0).toLocaleString()} DT`}
              trend={stats?.variationCA || "+ 0%"}
              trendType="up"
              colorClass="ca"
            />
            </ProtectedFinancialCard>
            <KPI_Card 
              label="Dépenses totales"
              value={`${(stats?.totalExpenses || 0).toLocaleString()} DT`}
              trend={stats?.variationExpenses || "+ 0%"}
              trendType="down"
              colorClass="expenses"
            />
            <ProtectedFinancialCard title="Résultat du mois protégé" lockedClassName="inv-kpi-card">
            <KPI_Card 
              label="Résultat du mois"
              value={`${(stats?.resultatNet || 0).toLocaleString()} DT`}
              trend={stats?.variationResultat || "+ 0%"}
              trendType={stats?.resultatNet >= 0 ? "up" : "down"}
              colorClass="result"
            />
            </ProtectedFinancialCard>
            <KPI_Card 
              label="Écart de stock"
              value="0 DT"
              trend="Flux stabilisé"
              trendType="neutral"
              colorClass="stock"
            />
          </div>

          <div className="inv-grid-main">
            
            <div className="inv-card-table">
              <div className="inv-card-header">
                <div className="inv-card-title">
                  <div>
                    <h2>Répartition Dynamique</h2>
                    <p>Filtre actif : {period === 'all' ? 'Totalité des flux' : period === 'month' ? 'Ce mois' : period === 'week' ? 'Cette semaine' : period === 'day' ? "Aujourd'hui" : 'Cette année'}</p>
                  </div>
                </div>
                <div className="inv-search-container">
                  <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>

              <div className="inv-table-wrapper">
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th>Catégorie</th>
                      <th>Flux</th>
                      <th>Montant (DT)</th>
                      <th>Budget (DT)</th>
                      <th>Variation (%)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map(item => (
                      <tr key={item.id}>
                        <td>
                          <div className="inv-category-cell">
                            <div className="inv-cat-icon-wrapper" style={{ color: item.color, background: `${item.color}15` }}>
                              {item.icon}
                            </div>
                            <div className="inv-cat-info-box">
                              <span style={{fontWeight: 700}}>{item.category}</span>
                              {item.type === 'Dépense' && item.variationPercent > 0 && (
                                <span className="inv-hors-budget-badge">
                                  <AlertTriangle size={10} /> HORS BUDGET
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td><span className={`inv-type-pill ${item.type === 'Dépense' ? 'expense' : 'revenue'}`}>{item.type}</span></td>
                        <td className="inv-td-amount">
                          <span style={{ color: item.type === 'Revenu' ? '#38A169' : '#E53E3E', fontWeight: 600 }}>{item.amount.toLocaleString()} DT</span>
                        </td>
                        <td className="inv-td-budget">
                          <span 
                            className="inv-editable-budget" 
                            title="Cliquer pour modifier" 
                            onClick={() => handleEditBudget(item)}
                          >
                            {item.budget > 0 ? `${item.budget.toLocaleString()} DT` : '—'}
                          </span>
                        </td>
                        <td className="inv-td-variation">
                          <span className={`inv-variation-tag ${
                            item.type === 'Revenu' 
                              ? (item.variationPercent >= 0 ? 'positive' : 'negative')
                              : (item.variationPercent <= 0 ? 'positive' : 'negative')
                          }`}>
                            {item.variationPercent > 0 ? '+' : ''}{item.variationPercent.toFixed(1)}%
                          </span>
                        </td>
                        <td>
                          <div className="inv-row-actions">
                            <button className="inv-action-btn view" title="Détails" onClick={() => handleViewDetails(item)}>Voir</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="inv-sidebar">
              <div className="inv-sidebar-card form-card">
                <div className="inv-card-header-sidebar">
                  <h3>Affectation commercial</h3>
                  <p>Commission calculée par puissance</p>
                </div>

                <form onSubmit={handleCommercialAssignmentSubmit} className="inv-new-expense-form">
                  <div className="inv-form-group">
                    <label>Client</label>
                    <select name="clientId" value={commercialAssignment.clientId} onChange={handleCommercialAssignmentChange}>
                      <option value="">Sélectionner un client</option>
                      {clientsForAssignment.map(client => (
                        <option key={client._id} value={client._id}>
                          {client.prenom || ''} {client.nom || ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="inv-form-group">
                    <label>Dossier</label>
                    <select
                      name="dossierId"
                      value={commercialAssignment.dossierId}
                      onChange={handleCommercialAssignmentChange}
                      disabled={!commercialAssignment.clientId}
                    >
                      <option value="">Sélectionner un dossier</option>
                      {assignmentDossiers.map(dossier => (
                        <option key={dossier._id} value={dossier._id}>
                          {dossier.referenceSteg || 'Pompage'} - {dossier.puissance || 0} kWc
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="inv-form-group">
                    <label>Commercial</label>
                    <select name="commercialId" value={commercialAssignment.commercialId} onChange={handleCommercialAssignmentChange}>
                      <option value="">Sélectionner un commercial</option>
                      {commerciauxForAssignment.map(commercial => (
                        <option key={commercial._id} value={commercial._id}>
                          {commercial.prenom || ''} {commercial.nom || ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedAssignmentDossier && (
                    <div className="inv-assignment-summary">
                      <span>Puissance dossier</span>
                      <strong>{selectedAssignmentDossier.puissance || 0} kWc</strong>
                      <small>
                        La commission sera appliquee depuis la grille du commercial pour cette puissance.
                      </small>
                    </div>
                  )}

                  <button type="submit" className="inv-btn-submit" disabled={isAssigningCommercial}>
                    {isAssigningCommercial ? 'Affectation...' : 'Affecter le commercial'}
                  </button>
                </form>
              </div>

              <div className="inv-sidebar-card form-card">
                <div className="inv-card-header-sidebar">
                  <h3>Nouvelle Dépense</h3>
                  <p>Enregistrement immédiat</p>
                </div>
                
                <form onSubmit={handleFormSubmit} className="inv-new-expense-form">
                  <div className="inv-form-group">
                    <label>Catégorie</label>
                    <select name="categorie" value={formState.categorie} onChange={handleInputChange}>
                      <option value="Equipe installation">Équipe installation</option>
                      <option value="Carburant">Carburant</option>
                      <option value="Assurance / Vignette">Assurance / Vignette</option>
                      <option value="Loyer">Loyer</option>
                      <option value="STEG & SONEDE">STEG & SONEDE</option>
                      <option value="Connexion Internet">Connexion Internet</option>
                      <option value="Achat des Produits">Achat des Produits</option>
                      <option value="Divers">Divers</option>
                      <option value="Sponsoring / Marketing">Sponsoring / Marketing</option>
                      <option value="Fournisseurs">Fournisseurs</option>
                      <option value="Bureau">Bureau</option>
                      <option value="Salaire employés">Salaire employés</option>
                      <option value="DF (Dépenses Fixes)">DF (Dépenses Fixes)</option>
                    </select>
                  </div>

                  <div className="inv-form-group">
                    <label>{isDivers ? 'Sous-catégorie' : isFournisseur ? 'Type de fournisseur' : 'Type de dépense'}</label>
                    {isDivers ? (
                      <select name="type" value={formState.type} onChange={handleInputChange}>
                        {DIVERS_SUBCATEGORIES.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    ) : isFournisseur ? (
                      <select name="type" value={formState.type} onChange={handleInputChange}>
                        {FOURNISSEUR_TYPES.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        ))}
                      </select>
                    ) : (
                      <input 
                        type="text" 
                        name="type" 
                        placeholder="Ex: Carburant, Loyer..." 
                        value={formState.type} 
                        onChange={handleInputChange}
                      />
                    )}
                  </div>

                  <div className="inv-form-group">
                    <label>Montant (DT)</label>
                    <input 
                      type="number" 
                      name="montant" 
                      placeholder="0.00" 
                      value={formState.montant} 
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="inv-form-group">
                    <label>Mode de paiement</label>
                    <select name="modePaiement" value={formState.modePaiement} onChange={handleInputChange}>
                      {MODE_PAIEMENT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {requiresRecepteur && (
                    <div className="inv-form-group">
                      <label>Nom du recepteur</label>
                      <input
                        type="text"
                        name="nomRecepteur"
                        placeholder="Ex: Mohamed Ben Ali"
                        value={formState.nomRecepteur}
                        onChange={handleInputChange}
                      />
                    </div>
                  )}

                  <div className="inv-form-group">
                    <label>Date de paiement</label>
                    <input 
                      type="date" 
                      name="datePaiement" 
                      value={formState.datePaiement} 
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="inv-form-group">
                    <label>Notes (Optionnel)</label>
                    <textarea 
                      name="notes" 
                      placeholder="Détails supplémentaires..." 
                      value={formState.notes} 
                      onChange={handleInputChange}
                    />
                  </div>

                  <button type="submit" className="inv-btn-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer la dépense'}
                  </button>
                </form>
              </div>

              <div className="inv-sidebar-card premium">
                <div className="inv-card-header-sidebar">
                  <h3>Répartition Dépenses</h3>
                  <p>Analyse par catégorie</p>
                </div>
                
                <div className="inv-pie-chart-container">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={pieData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                        {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pie-center-label">
                     <span>Total</span>
                     <p>{(stats?.totalExpenses || 0).toLocaleString()} DT</p>
                  </div>
                </div>

                <div className="inv-sidebar-actions">
                  <button className="inv-btn-validate-full" onClick={() => Swal.fire('Clôture', 'Génération des archives en cours...', 'info')}>
                    Clôturer la période
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="inv-footer">
            <p>© 2025 Sastra Energy — Système d'Inventaire Dynamique</p>
            <div className="inv-footer-info"><span>v3.0.0 (Dynamic)</span><div className="inv-dot" /><span>Données synchronisées en temps réel</span></div>
          </div>

        </div>
      </div>
    </div>
  );
};

const KPI_Card = ({ label, value, trend, trendType, colorClass }) => {
  return (
    <div className="inv-kpi-card">
      <div className="inv-kpi-header">
        <div className={`inv-kpi-icon-box ${colorClass}`} />
        <div className="inv-kpi-sparkline">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={SPARKLINE_DATA}>
              <Area type="monotone" dataKey="value" stroke={colorClass === 'expenses' ? '#E53E3E' : colorClass === 'result' ? '#38A169' : '#3182CE'} fill={colorClass === 'expenses' ? '#FFF5F5' : colorClass === 'result' ? '#F0FFF4' : '#EBF4FF'} strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="inv-kpi-info">
        <label>{label}</label>
        <div className="inv-kpi-value">{value}</div>
      </div>
      <div className="inv-kpi-footer">
        <div className={`inv-kpi-trend ${trendType}`}>
          {trend}
        </div>
      </div>
    </div>
  );
};

export default Inventaire;
